'use client'

import type { User } from 'korvo-app-prisma'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { ToastProvider } from '../shared/Toast/ToastProvider'
import { Toaster } from '../ui/toaster'
import { ActivityHistory } from './ActivityHistory'
import { AvatarUpload } from './AvatarUpload'
import { EmailChangeForm } from './EmailChangeForm'
import { MobileProfileHeader } from './MobileProfileHeader'
import { ModuleAnalytics } from './ModuleAnalytics'
import { MyWorkspacesTab } from './MyWorkspacesTab'
import { PasswordChangeForm } from './PasswordChangeForm'
import { PrivacyControls } from './PrivacyControls'
import { ProfileCompletion } from './ProfileCompletion'
import { ProfileEditForm } from './ProfileEditForm'
import { UserBudgetPanel } from './UserBudgetPanel'

type UserActivityItem = { id: string; userId: string; activity: string; details?: Record<string, unknown> | null; createdAt: Date }

export type UserProfileSlots = {
  header?: ReactNode
  extraSections?: ReactNode
  beforeForm?: ReactNode
  afterForm?: ReactNode
}

/** Identifies a tab within the UserProfile settings drawer. */
export type UserProfileTabId = string

const PROFILE_VALID_TABS = [
  'profile',
  'edit',
  'analytics',
  'security',
  'activity',
  'privacy',
  'my-connectors',
  'my-workspaces',
  'budget',
] as const

type ProfileTabId = (typeof PROFILE_VALID_TABS)[number]

function profileTabFromHash(h: string): ProfileTabId {
  const id = (h || 'profile').replace(/^#/, '')
  if ((PROFILE_VALID_TABS as readonly string[]).includes(id)) return id as ProfileTabId
  return 'profile'
}

function profileTabFromForced(id: string | undefined): ProfileTabId | null {
  if (!id) return null
  if ((PROFILE_VALID_TABS as readonly string[]).includes(id)) return id as ProfileTabId
  return null
}

type UserProfileProps = {
  user: User & {
    activities: UserActivityItem[]
    budgetUsedCents?: number
    budgetLimitCents?: number | null
  }
  /** Optional slots for injecting content (Extension Point C) */
  slots?: UserProfileSlots
  /** Tab to force-select on mount (used by URL-based section routing) */
  forcedTabId?: string
  /** Policy controlling which settings tabs are visible */
  settingsPolicy?: unknown
}

type ProfileData = {
  id: string
  name: string
  email: string
  bio: string
  website: string
  timezone: string
  language: string
  displayName: string
  pronouns: string
  interests: string[]
  emailNotifications: boolean
  pushNotifications: boolean
  marketingEmails: boolean
  profileVisibility: 'PUBLIC' | 'PRIVATE' | 'FRIENDS'
  profileCompleted: boolean
  avatar?: string
}

export function UserProfile({ user, slots, forcedTabId }: UserProfileProps) {
  const memberSince =
    user.createdAt !== undefined && user.createdAt !== null
      ? new Date(user.createdAt).toLocaleDateString('en-US')
      : ''
  const [activeTab, setActiveTab] = useState<ProfileTabId>(() => {
    const forced = profileTabFromForced(forcedTabId)
    if (forced) return forced
    return typeof window !== 'undefined' ? profileTabFromHash(window.location.hash) : 'profile'
  })
  useEffect(() => {
    const forced = profileTabFromForced(forcedTabId)
    if (forced) {
      setActiveTab(forced)
      return
    }
    const read = () =>
      setActiveTab(profileTabFromHash(typeof window !== 'undefined' ? window.location.hash : ''))
    read()
    window.addEventListener('hashchange', read)
    return () => window.removeEventListener('hashchange', read)
  }, [forcedTabId])
  const [profileData, setProfileData] = useState<ProfileData>({
    id: user.id,
    name: user.name !== undefined && user.name !== null ? user.name : '',
    email: user.email,
    bio: user.bio !== undefined && user.bio !== null ? user.bio : '',
    website: user.website !== undefined && user.website !== null ? user.website : '',
    timezone: user.timezone !== undefined && user.timezone !== null ? user.timezone : '',
    language: user.language !== undefined && user.language !== null ? user.language : '',
    displayName: user.displayName !== undefined && user.displayName !== null ? user.displayName : '',
    pronouns: user.pronouns !== undefined && user.pronouns !== null ? user.pronouns : '',
    interests: user.interests || [],
    emailNotifications: user.emailNotifications ?? true,
    pushNotifications: user.pushNotifications ?? true,
    marketingEmails: user.marketingEmails ?? false,
    profileVisibility: (user.profileVisibility || 'PRIVATE') as 'PRIVATE' | 'PUBLIC' | 'FRIENDS',
    profileCompleted: user.profileCompleted || false,
    avatar: user.avatar || undefined
  })

  const handleProfileUpdate = (profileDataChanges: Partial<ProfileData>) => {
    setProfileData(prev => ({ ...prev, ...profileDataChanges }))
  }

  const handleAvatarChange = (avatar: string | null) => {
    setProfileData(prev => ({ ...prev, avatar: avatar || undefined }))
  }

  return (
    <ToastProvider>
    <Toaster />
    <div className="min-h-screen bg-background-default">
      {slots?.header}
      {/* Mobile: simple title only; tabs are in the site side menu (same on desktop) */}
      <MobileProfileHeader />

      <div className="max-w-4xl mx-auto p-4 lg:p-6">
        <div className="bg-background-secondary rounded-lg shadow">
          {/* Header (desktop and mobile) */}
          <div className="px-6 py-4 border-b border-border">
            <h1 className="text-2xl font-semibold text-foreground-default">Profile Settings</h1>
            <p className="text-foreground-muted">Manage your account settings and preferences</p>
          </div>
          {slots?.beforeForm}

          {/* Tab Content */}
          <div className="p-4 lg:p-6">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {/* Profile Header with Avatar */}
                <div className="flex flex-col items-center sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                  <AvatarUpload
                    currentAvatar={profileData.avatar}
                    onAvatarChange={handleAvatarChange}
                  />
                  <div className="flex-1 text-center sm:text-left">
                    <h2 className="text-xl sm:text-2xl font-bold text-foreground-default">
                      {profileData.displayName || profileData.name || 'Anonymous User'}
                    </h2>
                    {profileData.pronouns && (
                      <p className="text-sm text-foreground-muted">{profileData.pronouns}</p>
                    )}
                    <p className="text-sm text-foreground-muted break-all">{profileData.email}</p>
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'ADMIN' 
                          ? 'bg-danger-default text-danger-foreground'
                          : user.role === 'USER'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background-muted text-foreground-default border border-border'
                      }`}>
                        {user.role}
                      </span>
                      {profileData.profileCompleted && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-success-default text-success-foreground">
                          Profile Complete
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bio */}
                {profileData.bio && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground-default mb-2">About</h3>
                    <p className="text-sm text-foreground-default whitespace-pre-wrap">{profileData.bio}</p>
                  </div>
                )}

                {/* Profile Details */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {profileData.website && (
                    <div>
                      <label className="block text-sm font-medium text-foreground-default">Website</label>
                      <a 
                        href={profileData.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="mt-1 text-sm text-primary hover:text-primary-hover"
                      >
                        {profileData.website}
                      </a>
                    </div>
                  )}
                  {profileData.timezone && (
                    <div>
                      <label className="block text-sm font-medium text-foreground-default">Timezone</label>
                      <div className="mt-1 text-sm text-foreground-default">{profileData.timezone}</div>
                    </div>
                  )}
                  {profileData.language && (
                    <div>
                      <label className="block text-sm font-medium text-foreground-default">Language</label>
                      <div className="mt-1 text-sm text-foreground-default">{profileData.language.toUpperCase()}</div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-foreground-default">Member Since</label>
                    <div className="mt-1 text-sm text-foreground-default">
                      {memberSince}
                    </div>
                  </div>
                </div>

                {/* Interests */}
                {profileData.interests.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground-default mb-2">Interests</h3>
                    <div className="flex flex-wrap gap-2">
                      {profileData.interests.map((interest, index) => (
                        <span
                          key={index}
                          className="inline-flex px-2 py-1 text-xs font-medium bg-background-muted text-foreground-default rounded-full"
                        >
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Profile Completion */}
                <ProfileCompletion userId={user.id} profileCompleted={profileData.profileCompleted} />
                <UserBudgetPanel
                  budgetUsedCents={user.budgetUsedCents ?? 0}
                  budgetLimitCents={user.budgetLimitCents}
                />
                {slots?.extraSections}
              </div>
            )}

            {activeTab === 'edit' && (
              <ProfileEditForm
                initialData={profileData}
                onUpdate={handleProfileUpdate}
              />
            )}

            {activeTab === 'analytics' && (
              <ModuleAnalytics userId={user.id} />
            )}

            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-foreground-default">Security Settings</h3>
                  <p className="text-sm text-foreground-muted">Manage your password and security preferences</p>
                </div>

                <div className="space-y-2">
                  <h3 className="text-base font-medium text-foreground-default">Email Address</h3>
                  <p className="text-sm text-foreground-muted">Current: {profileData.email}</p>
                  <EmailChangeForm currentEmail={profileData.email} />
                </div>

                <PasswordChangeForm />
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-foreground-default">Activity History</h3>
                  <p className="text-sm text-foreground-muted">Recent account activity and login history</p>
                </div>
                
                <ActivityHistory activities={user.activities} />
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-foreground-default">Privacy & Data Control</h3>
                  <p className="text-sm text-foreground-muted">Manage your data and privacy settings</p>
                </div>
                
                <PrivacyControls />
              </div>
            )}

            {activeTab === 'my-connectors' && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-2xl font-semibold text-foreground-default">My Connectors</h1>
                  <p className="text-foreground-muted">Manage your personal data connectors</p>
                </div>
              </div>
            )}

            {activeTab === 'my-workspaces' && <MyWorkspacesTab />}

            {activeTab === 'budget' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-foreground-default">Budget</h3>
                  <p className="text-sm text-foreground-muted">Usage and requests for a higher allocation</p>
                </div>
                <UserBudgetPanel
                  budgetUsedCents={user.budgetUsedCents ?? 0}
                  budgetLimitCents={user.budgetLimitCents}
                />
              </div>
            )}
          {slots?.afterForm}
          </div>
        </div>
      </div>
    </div>
    </ToastProvider>
  )
}